// TODO(PLAT-8292): v1 external legend for the indoor-climate chart.
//
// The native Highcharts legend can't cleanly handle 27 sensors × 2 series + a
// few global series — items wrap into the plot area and there's no native way
// to add section headers between categories. This component renders a custom,
// sectioned, scrollable legend beside the chart so the plot area is never
// compressed and outdoor / indoor-temperature / indoor-humidity stay visually
// grouped.
//
// Revisit when we have a shared pattern for many-series charts across PropAI
// (e.g. promote to a generic `<ChartLegend />` next to BaseTimeSeriesChart).

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, useTheme } from "@mui/material";

import type { TimeSeriesData } from "../charts/BaseTimeSeriesChart";
import { IndoorClimateSeriesType } from "./IndoorClimateChartContainer.types";

type LegendSection = {
  key: "outdoor" | "indoorTemp" | "indoorHumidity";
  title: string;
  items: TimeSeriesData[];
};

type Props = {
  series: TimeSeriesData[];
  onSeriesToggle?: (uid: string) => void;
  title?: string;
};

function categorize(uid: string): LegendSection["key"] {
  if (uid === IndoorClimateSeriesType.OUTDOOR_TEMP) return "outdoor";
  if (uid === IndoorClimateSeriesType.AVERAGE_HUMID || uid.startsWith("humid_")) {
    return "indoorHumidity";
  }
  return "indoorTemp";
}

export const IndoorClimateChartLegend: React.FC<Props> = ({ series, onSeriesToggle, title }) => {
  const { t } = useTranslation(["propai", "propai_properties", "_common"]);
  const theme = useTheme();

  const sections = useMemo<LegendSection[]>(() => {
    const grouped: Record<LegendSection["key"], TimeSeriesData[]> = {
      outdoor: [],
      indoorTemp: [],
      indoorHumidity: [],
    };

    series.forEach((s) => {
      grouped[categorize(s.uid)].push(s);
    });

    const all: LegendSection[] = [
      {
        key: "outdoor",
        title: t("_common:text_outdoor_temperature"),
        items: grouped.outdoor,
      },
      {
        key: "indoorTemp",
        title: t("indoor_temperature"),
        items: grouped.indoorTemp,
      },
      {
        key: "indoorHumidity",
        title: t("indoor_humidity"),
        items: grouped.indoorHumidity,
      },
    ];
    return all.filter((section) => section.items.length > 0);
  }, [series, t]);

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        pr: 1,
        borderLeft: `1px solid ${theme.palette.divider}`,
        pl: 2,
      }}
    >
      {title && (
        <Typography
          variant="titleSmall"
          sx={{
            display: "block",
            color: theme.palette.text.primary,
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
      )}
      <Stack spacing={2}>
        {sections.map((section) => (
          <Box key={section.key}>
            <Typography
              variant="labelMedium"
              sx={{
                textTransform: "uppercase",
                color: theme.palette.text.secondary,
                letterSpacing: 0.4,
                mb: 0.5,
                display: "block",
              }}
            >
              {section.title}
            </Typography>
            <Stack spacing={0.25}>
              {section.items.map((item) => {
                const visible = item.visible !== false;
                return (
                  <Box
                    key={item.uid}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSeriesToggle?.(item.uid)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSeriesToggle?.(item.uid);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      opacity: visible ? 1 : 0.45,
                      "&:hover": { backgroundColor: theme.palette.action.hover },
                      userSelect: "none",
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: item.type === "arearange" ? 10 : 3,
                        backgroundColor: (item.color as string) ?? theme.palette.text.primary,
                        borderRadius: item.type === "arearange" ? 0.5 : 1,
                        opacity: item.type === "arearange" ? 0.5 : 1,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="bodySmall"
                      sx={{
                        textDecoration: visible ? "none" : "line-through",
                        color: theme.palette.text.primary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
